// src/pages/data-operations/AnalyticsDataPage.tsx
// Data Operations > Analytics data: is the analytics data healthy, when did it
// last refresh, and "Run now". A refresh shows as four steps while it runs; the
// history below opens any earlier one.

import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { Check, Close, PlayArrow } from '@mui/icons-material';
import { C, Card, Chip, Grid, Head, Page, Table, TrendTile, type Tone } from '../insights/designKit';
import { warehouseService, type MonitorCheck, type MonitorRun, type MonitorStep } from '../../services/warehouseService';

const RUN_TONE: Record<MonitorRun['status'], { text: string; tone: Tone }> = {
  COMPLETED: { text: 'Completed', tone: 'green' },
  RUNNING: { text: 'Running', tone: 'blue' },
  FAILED: { text: 'Failed', tone: 'red' },
  STALLED: { text: 'Stalled', tone: 'red' },
};
const CHECK_TONE: Record<MonitorCheck['state'], { color: string; soft: string }> = {
  ok: { color: C.green, soft: C.greenSoft },
  warn: { color: C.amber, soft: C.amberSoft },
  bad: { color: C.red, soft: C.redSoft },
  unknown: { color: C.muted, soft: C.track },
};

const when = (iso: string | null): string => (iso ? new Date(iso).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const int = (v: number | null): string => (v == null ? '' : v.toLocaleString('en-US'));
const money = (v: number | null): string => (v == null ? '' : v < 0.01 ? '<$0.01' : `$${v.toFixed(2)}`);
function duration(sec: number | null): string {
  if (sec == null) return '';
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)} min ${sec % 60}s`;
}
function ago(iso: string | null): string {
  if (!iso) return 'Never';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 2) return 'Just now';
  if (min < 90) return `${min} min ago`;
  const h = Math.round(min / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}

function StepMark({ state }: { state: MonitorStep['state'] }) {
  const base = { width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } as const;
  if (state === 'done') return <Box sx={{ ...base, bgcolor: C.green, color: '#fff' }}><Check sx={{ fontSize: 17 }} /></Box>;
  if (state === 'failed') return <Box sx={{ ...base, bgcolor: C.red, color: '#fff' }}><Close sx={{ fontSize: 17 }} /></Box>;
  if (state === 'running') return <Box sx={{ ...base, border: `2px solid ${C.blue}`, boxSizing: 'border-box' }}><CircularProgress size={14} thickness={6} /></Box>;
  return <Box sx={{ ...base, border: `2px solid ${C.line}`, boxSizing: 'border-box' }} />;
}

const STEP_WORD: Record<MonitorStep['state'], string> = { done: 'Done', running: 'Running', failed: 'Failed', waiting: 'Waiting' };

function Steps({ run }: { run: MonitorRun }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: { xs: 2, md: 0 } }}>
      {run.steps.map((step, i) => (
        <Box key={step.key} sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: 1.25, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StepMark state={step.state} />
            {i < run.steps.length - 1 && <Box sx={{ display: { xs: 'none', md: 'block' }, flex: 1, height: 2, bgcolor: step.state === 'done' ? C.green : C.line, mr: 1 }} />}
          </Box>
          <Box sx={{ minWidth: 0, pr: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: step.state === 'waiting' ? C.muted : C.ink }}>{step.label}</Typography>
            <Typography sx={{ fontSize: 12.5, color: step.state === 'failed' ? C.red : C.muted, mt: 0.25 }}>{step.detail ?? STEP_WORD[step.state]}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function AnalyticsDataPage() {
  const client = useQueryClient();
  const [selected, setSelected] = React.useState<string | null>(null);
  const monitor = useQuery({
    queryKey: ['warehouse-monitor'],
    queryFn: () => warehouseService.monitor(),
    // Follow a refresh closely while it runs.
    refetchInterval: (query) => (query.state.data?.runs.some((r) => r.status === 'RUNNING') ? 5_000 : 60_000),
  });
  const start = useMutation({
    mutationFn: () => warehouseService.startRun(),
    onSuccess: () => {
      setSelected(null);
      void client.invalidateQueries({ queryKey: ['warehouse-monitor'] });
    },
  });

  const data = monitor.data;
  const runs = data?.runs ?? [];
  const latest = runs[0];
  const shown = runs.find((r) => r.runId === selected) ?? latest;
  const lastDone = runs.find((r) => r.status === 'COMPLETED');
  const running = latest?.status === 'RUNNING';
  const problems = (data?.checks ?? []).filter((c) => c.state === 'bad').length;
  const warnings = (data?.checks ?? []).filter((c) => c.state === 'warn').length;
  const overall: { text: string; tone: Tone } = running
    ? { text: 'Refreshing', tone: 'blue' }
    : problems > 0
      ? { text: `${problems} ${problems === 1 ? 'problem' : 'problems'}`, tone: 'red' }
      : warnings > 0
        ? { text: `${warnings} to look at`, tone: 'amber' }
        : { text: 'Healthy', tone: 'green' };
  const startError = start.error as { response?: { data?: { message?: string } } } | null;

  return (
    <Page>
      <Head
        title="Analytics data"
        sub={
          <>
            {data && <Chip text={overall.text} tone={overall.tone} dot />}
            <span>Copies catalog data into the warehouse and rebuilds every report</span>
          </>
        }
        right={
          data?.canRun ? (
            <Tooltip title={running ? 'A refresh is running' : 'Refresh everything now. Takes a few minutes.'} arrow>
              <span>
                <Box
                  component="button"
                  disabled={running || start.isPending}
                  onClick={() => start.mutate()}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 40, px: 2.25, borderRadius: 2, border: 0, bgcolor: C.blue, color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer', '&:disabled': { bgcolor: C.grey, cursor: 'default' } }}
                >
                  {running || start.isPending ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <PlayArrow sx={{ fontSize: 18 }} />}
                  {running ? 'Running' : 'Run now'}
                </Box>
              </span>
            </Tooltip>
          ) : undefined
        }
      />

      {startError && <Alert severity="warning">{startError.response?.data?.message ?? 'The refresh could not start.'}</Alert>}
      {monitor.error && <Alert severity="warning">Could not read the analytics status.</Alert>}
      {monitor.isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={24} /></Box>}

      {data && (
        <>
          <Grid cols={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}>
            <TrendTile label="Last refresh" value={ago(lastDone?.finishedAt ?? null)} note={lastDone ? `${when(lastDone.finishedAt)} · took ${duration(lastDone.durationSec)}` : 'No refresh has completed'} />
            <TrendTile
              label="Next refresh"
              value={data.schedule.paused ? 'Paused' : data.schedule.nextAt ? when(data.schedule.nextAt) : 'Not scheduled'}
              tone={data.schedule.on ? undefined : 'amber'}
              note={data.schedule.on && data.schedule.everyHours ? `Every ${data.schedule.everyHours} hours` : 'Runs only when started by hand'}
            />
            <TrendTile label="Rows copied" value={int(lastDone?.rows ?? null) || '0'} note={lastDone?.tables ? `${lastDone.tables} tables` : ''} />
            <TrendTile label="Cost of a refresh" value={money(lastDone?.costUsd ?? null) || '$0'} note="BigQuery, last refresh" />
          </Grid>

          {shown && (
            <Card
              title={shown.runId === latest?.runId ? 'Latest refresh' : 'Refresh'}
              count={`${when(shown.startedAt)}${shown.durationSec != null ? ` · ${duration(shown.durationSec)}` : ''}`}
              link={<Chip text={RUN_TONE[shown.status].text} tone={RUN_TONE[shown.status].tone} dot />}
            >
              <Steps run={shown} />
              {shown.error && <Alert severity="error" sx={{ mt: 1 }}>{shown.error}</Alert>}
              {shown.failed.length > 0 && (
                <Box sx={{ fontSize: 13, color: C.ink2 }}>
                  <Box component="span" sx={{ color: C.muted }}>Did not build: </Box>
                  {shown.failed.join(', ')}
                </Box>
              )}
            </Card>
          )}

          <Grid cols="1fr 1fr">
            <Card title="Health">
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {data.checks.map((check) => (
                  <Box key={check.key} sx={{ display: 'flex', gap: 1.5, py: 1.25, borderBottom: `1px solid ${C.line2}`, '&:last-of-type': { borderBottom: 0 } }}>
                    <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: CHECK_TONE[check.state].soft, color: CHECK_TONE[check.state].color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: '1px', fontSize: 13, fontWeight: 700 }}>
                      {check.state === 'ok' ? <Check sx={{ fontSize: 15 }} /> : check.state === 'unknown' ? '?' : '!'}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 14, color: C.ink }}>{check.label}</Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 600, color: check.state === 'ok' ? C.ink : CHECK_TONE[check.state].color }}>{check.value}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 12.5, color: C.muted, mt: 0.25 }}>{check.detail}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Card>

            <Card title="History" count={runs.length}>
              <Box sx={{ overflow: 'auto', maxHeight: 420 }}>
                <Box sx={{ minWidth: 460 }}>
                  <Table
                    columns={[{ label: 'Started', width: '34%' }, { label: 'Took', right: true }, { label: 'Rows', right: true }, { label: 'Cost', right: true }, { label: 'Result', width: '24%' }]}
                    rows={runs.map((r) => ({
                      key: r.runId,
                      to: r.runId,
                      cells: [
                        <Box component="span" sx={{ fontWeight: r.runId === shown?.runId ? 600 : 400 }}>{when(r.startedAt)}</Box>,
                        duration(r.durationSec),
                        int(r.rows),
                        money(r.costUsd),
                        <Chip text={RUN_TONE[r.status].text} tone={RUN_TONE[r.status].tone} dot />,
                      ],
                    }))}
                    onRow={(runId) => setSelected(runId)}
                  />
                </Box>
              </Box>
            </Card>
          </Grid>
        </>
      )}
    </Page>
  );
}
